import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';

/** Vertical extent of a list row at drag start (viewport coordinates). */
export interface RowBox {
  top: number;
  height: number;
}

export interface ListDragState {
  /** Index of the row being dragged. */
  from: number;
  /** Index it would land on if dropped now. */
  to: number;
  /** How far the dragged row has moved from its start, in px. */
  offset: number;
  /** Height of the dragged row — the gap the other rows open up. */
  height: number;
  /**
   * `settling` = dropped, waiting for the reordered list to arrive: the rows
   * keep their dragged positions so they never flash back to the old order.
   */
  phase: 'dragging' | 'settling';
}

/** Distance from the list's edge (px) at which dragging auto-scrolls it. */
const AUTOSCROLL_EDGE_PX = 28;
const AUTOSCROLL_STEP_PX = 10;
/** Give up waiting for the reordered list after this long (e.g. no-op move). */
const SETTLE_TIMEOUT_MS = 300;

const isMac = (): boolean =>
  typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);

/**
 * Slot the dragged row would drop into. A neighbour gives way once the dragged
 * row's leading edge crosses that neighbour's midpoint (half overlap), so the
 * gap opens under the pointer instead of lagging a full row behind.
 */
export function computeDropIndex(
  rows: readonly RowBox[],
  from: number,
  offset: number
): number {
  const dragged = rows[from];
  if (!dragged) return from;
  const top = dragged.top + offset;
  const bottom = top + dragged.height;
  let to = from;
  rows.forEach((row, index) => {
    const middle = row.top + row.height / 2;
    if (index < from && top < middle) to -= 1;
    if (index > from && bottom > middle) to += 1;
  });
  return to;
}

/**
 * How far (px) a non-dragged row slides to open a gap for the dragged one:
 * rows between the origin and the drop slot shift one dragged-row height.
 */
export function rowShift(index: number, drag: ListDragState): number {
  const { from, to, height } = drag;
  if (index === from) return 0;
  if (from < to && index > from && index <= to) return -height;
  if (from > to && index >= to && index < from) return height;
  return 0;
}

export interface UseListReorderDragOptions {
  /**
   * The list being reordered. A new identity while dragging cancels the drag
   * (indices would be stale); after a drop it is the reordered list arriving,
   * which ends the settle phase.
   */
  items: unknown;
  /** Row elements in list order, measured when a drag starts. */
  getRowElements: () => (HTMLElement | undefined)[];
  /** Scrollable list container (auto-scrolled near its edges). */
  scrollContainerRef: RefObject<HTMLElement | null>;
  onDrop: (from: number, to: number) => void;
}

export interface UseListReorderDragResult {
  drag: ListDragState | null;
  /** Wire to the drag handle's `onPointerDown`. */
  startDrag: (index: number, event: ReactPointerEvent<HTMLElement>) => void;
}

/**
 * Pointer-driven list reordering: the grabbed row follows the pointer (fully
 * opaque — no browser drag ghost) while the rows it passes slide aside to open
 * its drop slot. Escape, a cancelled / lost pointer, focus loss or the list
 * changing underneath aborts without moving anything.
 */
export function useListReorderDrag({
  items,
  getRowElements,
  scrollContainerRef,
  onDrop,
}: UseListReorderDragOptions): UseListReorderDragResult {
  // A drag remembers the list it started on; once `items` changes it is inert
  // (derived below, during render — so the reordered list and the cleared
  // transforms land in the same commit, with no flash of the old order).
  type TrackedDrag = ListDragState & { items: unknown };
  const [tracked, setTracked] = useState<TrackedDrag | null>(null);
  const dragRef = useRef<TrackedDrag | null>(null);
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  });
  const drag: ListDragState | null =
    tracked && tracked.items === items
      ? {
          from: tracked.from,
          to: tracked.to,
          offset: tracked.offset,
          height: tracked.height,
          phase: tracked.phase,
        }
      : null;
  const geometry = useRef<{
    pointerId: number;
    startY: number;
    startScroll: number;
    maxScroll: number;
    rows: RowBox[];
  } | null>(null);
  const onDropRef = useRef(onDrop);
  useEffect(() => {
    onDropRef.current = onDrop;
  });

  const update = useCallback((next: TrackedDrag | null): void => {
    dragRef.current = next;
    setTracked(next);
  }, []);

  const startDrag = useCallback(
    (index: number, event: ReactPointerEvent<HTMLElement>): void => {
      // Primary button only; ctrl+click on macOS opens the context menu,
      // which swallows the pointerup and would strand the drag.
      if (event.button !== 0 || (event.ctrlKey && isMac())) return;
      const active = dragRef.current;
      if (active?.phase === 'dragging' && active.items === itemsRef.current) {
        return;
      }
      const rows = getRowElements().map(element => {
        const rect = element?.getBoundingClientRect();
        return { top: rect?.top ?? 0, height: rect?.height ?? 0 };
      });
      if (!rows[index]) return;
      // Keep the page from selecting text / scrolling under the drag.
      event.preventDefault();
      const list = scrollContainerRef.current;
      geometry.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startScroll: list?.scrollTop ?? 0,
        maxScroll: list
          ? Math.max(list.scrollHeight - list.clientHeight, 0)
          : 0,
        rows,
      };
      update({
        from: index,
        to: index,
        offset: 0,
        height: rows[index].height,
        phase: 'dragging',
        items: itemsRef.current,
      });
    },
    [getRowElements, scrollContainerRef, update]
  );

  const settling = drag?.phase === 'settling';
  useEffect(() => {
    if (!settling) return undefined;
    const timer = setTimeout(() => update(null), SETTLE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [settling, update]);

  const dragging = drag?.phase === 'dragging';
  useEffect(() => {
    if (!dragging) return undefined;

    const finish = (commit: boolean): void => {
      const current = dragRef.current;
      geometry.current = null;
      if (commit && current && current.to !== current.from) {
        update({ ...current, phase: 'settling' });
        onDropRef.current(current.from, current.to);
      } else {
        update(null);
      }
    };
    const isOurs = (event: PointerEvent): boolean =>
      geometry.current !== null &&
      (event.pointerId === undefined ||
        event.pointerId === geometry.current.pointerId);

    const move = (event: PointerEvent): void => {
      const current = dragRef.current;
      const geo = geometry.current;
      if (!current || !geo || !isOurs(event)) return;
      // Button no longer held (the release happened outside our view).
      if (event.buttons !== undefined && (event.buttons & 1) === 0) {
        finish(false);
        return;
      }
      const list = scrollContainerRef.current;
      if (list && geo.maxScroll > 0) {
        const bounds = list.getBoundingClientRect();
        const step =
          event.clientY < bounds.top + AUTOSCROLL_EDGE_PX
            ? -AUTOSCROLL_STEP_PX
            : event.clientY > bounds.bottom - AUTOSCROLL_EDGE_PX
              ? AUTOSCROLL_STEP_PX
              : 0;
        // Clamp to the list's real extent: the translated row would otherwise
        // grow the scroll area and let the list scroll away into blank space.
        if (step !== 0) {
          list.scrollTop = Math.min(
            Math.max(list.scrollTop + step, 0),
            geo.maxScroll
          );
        }
      }
      // Scrolling the list moves the rows under the pointer; fold it in.
      const scrolled = (list?.scrollTop ?? 0) - geo.startScroll;
      const offset = event.clientY - geo.startY + scrolled;
      update({
        ...current,
        offset,
        to: computeDropIndex(geo.rows, current.from, offset),
      });
    };
    const up = (event: PointerEvent): void => {
      if (isOurs(event)) finish(true);
    };
    const pointerCancel = (event: PointerEvent): void => {
      if (isOurs(event)) finish(false);
    };
    const cancel = (): void => finish(false);
    const key = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      // Cancel the drag only — don't also close the surrounding popover
      // (Radix's dismiss handler skips default-prevented Escape events).
      event.stopPropagation();
      event.preventDefault();
      finish(false);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', pointerCancel);
    window.addEventListener('blur', cancel);
    window.addEventListener('contextmenu', cancel);
    window.addEventListener('keydown', key, true);
    const { userSelect } = document.body.style;
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', pointerCancel);
      window.removeEventListener('blur', cancel);
      window.removeEventListener('contextmenu', cancel);
      window.removeEventListener('keydown', key, true);
      document.body.style.userSelect = userSelect;
    };
  }, [dragging, scrollContainerRef, update]);

  return { drag, startDrag };
}
